<template>
  <BbPopup
    v-model="referencesListOpened.open"
    :showCloseIcon="true"
    contentClass="references-list-popup layouts-list-popup"
  >
    <template #popup-header>
      <div class="references-list-popup__header">
        <div class="references-list-popup__title">
          <TextBody tag="span" i18n-key="author.manageReferences" :body-type="2" weight="medium" />
        </div>
        <template v-if="referencesListOpened.editable">
          <div class="references-list-popup__divider" />
          <div class="references-list-popup__footnotes-row">
            <div class="references-list-popup__footnotes-label">{{ t('author.displayReferences') }}</div>
            <BbSwitch
              v-model="footnotesPositionValue"
              :items="footnotesPositionValues"
              class="references-list-popup__switch"
            />
          </div>
          <div class="references-list-popup__divider" />
        </template>
        <BbInput
          v-model="filterValue"
          class="references-list-popup__search"
          size="small"
          :placeholder="t('common.search')"
        >
          <template #prepend-inner>
            <BbIcon size="small" name="search" />
          </template>
        </BbInput>
      </div>
    </template>
    <ReferencesList
      :is-editable="referencesListOpened.editable"
      :filter-value="filterValue"
      @update-references="refreshReferences"
    />
    <div v-if="referencesListOpened.editable" class="references-list-popup__add-reference">
      <BbBtn
        v-if="!isAddingReference"
        class="references-list-popup__add-reference-btn"
        type="secondary"
        size="small"
        @click="isAddingReference = true"
      >
        <BbIcon name="plus-small" size="small" />
        <TextBody i18n-key="author.addNewReference" />
      </BbBtn>
      <div v-else class="references-list-popup__add-reference-form">
        <BbTextarea
          v-model="newReferenceText"
          class="references-list-popup__add-reference-input"
          variant="outlined"
          :rows="1"
          :max-rows="6"
          :auto-grow="true"
          :placeholder="t('author.typeReferencePlaceholder')"
        />
        <div class="references-list-popup__add-reference-actions">
          <BbBtn type="secondary" size="small" @click="cancelAddReference">
            <TextBody i18n-key="common.cancel" />
          </BbBtn>
          <BbBtn
            type="primary"
            size="small"
            data-attr="reference-added-confirm"
            :is-disabled="!newReferenceText.trim()"
            @click="saveNewReference"
          >
            <TextBody i18n-key="common.save" />
          </BbBtn>
        </div>
      </div>
    </div>
  </BbPopup>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { useWesley } from '@anthill-modules/wesley-components';
import { v4 as uuidv4 } from 'uuid';

import { GetUserData } from '~/constants/getters';

import ReferencesList from '~/components/conversation/authoring/references/ReferencesList.vue';
import TextBody from '~/components/common/TextBody.vue';
import useModals from '~/composables/useModals';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useAuthoring from '~/composables/useAuthoring';
import useSecondOrderReferencesGating from '~/composables/useSecondOrderReferencesGating';
import store from '~/store';
import {
  displayAllFreshReferencesAtTheEnd,
  removeOrHideDocumentReferences,
  updateDocumentLayoutsShowFootnotesToFalse,
} from '~/utils/footnotes';

export default {
  components: {
    TextBody,
    ReferencesList,
  },
  setup() {
    const { t } = useI18n();

    const { referencesListOpened } = useModals();
    const {
      sendMessage: sendMessageToDocument,
      asyncSendMessage: sendAsyncMessageToDocument,
      iframeWindow,
    } = useWesley();
    const { currentDesignSystemDocumentElements, updateEditorSelectableElementsData, refreshReferencesOnUpdate } =
      useDocumentEditor();
    const { setDisplayReferencesOnDocumentLevel, footnotes, setFootnotes, displayReferencesOnDocumentLevel } =
      useAuthoring();
    const { isSecondOrderReferencesOrg } = useSecondOrderReferencesGating();
    const userData = computed(() => store.getters[`authStore/${GetUserData}`]);

    const footnotesPositionValues = [
      {
        title: t('author.showReferencesBlockLevel'),
        value: 0,
      },
      {
        title: t('author.showReferencesDocumentLevel'),
        value: 1,
      },
    ];

    const iframeBody = ref(null);
    const footnotesPositionValue = ref(displayReferencesOnDocumentLevel.value ? 1 : 0);
    const filterValue = ref('');

    watch(
      () => [iframeWindow.value, referencesListOpened.value],
      async ([newIframeWindow, opened]) => {
        if (opened.open) {
          iframeBody.value = newIframeWindow?.document?.body || null;
          const displayOnDocumentLevel =
            iframeBody.value && iframeBody.value.attributes?.['show-footnotes-on-document']?.value === 'true' ? 1 : 0;
          footnotesPositionValue.value = displayOnDocumentLevel;
          setDisplayReferencesOnDocumentLevel(displayOnDocumentLevel === 1);
        }
      },
      { immediate: true, deep: true },
    );

    watch(footnotesPositionValue, () => {
      if (iframeBody?.value?.attributes?.id?.value) {
        sendMessageToDocument({
          method: 'updateAttribute',
          params: {
            query: `#${iframeBody.value.attributes.id.value}`,
            attr: 'show-footnotes-on-document',
            value: footnotesPositionValue.value === 1,
          },
        });
        iframeBody.value.setAttribute(
          'show-footnotes-on-document',
          footnotesPositionValue.value === 1 ? 'true' : 'false',
        );
        setDisplayReferencesOnDocumentLevel(footnotesPositionValue.value === 1);

        // if footnotes are set to be shown on document level, hide them from layouts, and display all footnotes at the end
        if (footnotesPositionValue.value === 1) {
          updateDocumentLayoutsShowFootnotesToFalse({
            currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
            sendMessageToDocument,
          });
          // we have to wait until references are removed from dom
          setTimeout(() => {
            displayAllFreshReferencesAtTheEnd({
              htmlString: iframeBody.value?.innerHTML,
              currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
              footnotes: footnotes.value,
              sendMessageToDocument,
              sendAsyncMessageToDocument,
              t,
              showSecondOrderReferences: isSecondOrderReferencesOrg.value,
            });
          }, 500);
        } else {
          removeOrHideDocumentReferences({
            currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
            sendMessageToDocument,
          });
        }
        updateEditorSelectableElementsData();
      }
    });

    const refreshReferences = () => {
      if (iframeBody.value?.innerHTML) {
        setTimeout(() => {
          refreshReferencesOnUpdate();
        }, 2000);
      }
    };

    const isAddingReference = ref(false);
    const newReferenceText = ref('');

    const cancelAddReference = () => {
      newReferenceText.value = '';
      isAddingReference.value = false;
    };

    const saveNewReference = () => {
      const text = newReferenceText.value.trim();
      if (!text) return;

      const newReference = {
        id: uuidv4(),
        addedByUser: userData.value,
        name: text,
        customFootnote: text,
        pageNumber: [1],
        index: footnotes.value?.length || 0,
      };

      setFootnotes([...(footnotes.value || []), newReference]);
      cancelAddReference();
    };

    return {
      filterValue,
      footnotesPositionValue,
      footnotesPositionValues,
      referencesListOpened,
      refreshReferences,
      isAddingReference,
      newReferenceText,
      cancelAddReference,
      saveNewReference,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.references-list-popup {
  &__header {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__title {
    padding: $bb-spacing-2 0;

    :deep(span) {
      color: $bb-neutral-1 !important;
      font-weight: 700 !important;
    }
  }

  &__divider {
    height: 1px;
    background: $bb-neutral-4;
    margin: $bb-spacing-2 (-$bb-spacing-8);
  }

  &__footnotes-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: $bb-spacing-2;
    padding: $bb-spacing-2 0;
  }

  &__footnotes-label {
    color: $bb-neutral-2;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__search {
    width: 100%;
    margin-top: $bb-spacing-2;
  }

  &__switch {
    pointer-events: inherit;
    background: transparent;
    flex: 1 1 auto;

    :deep(.switch-item) {
      padding: $bb-spacing-1 !important;
      border-radius: $bb-spacing-2 !important;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content) {
      button {
        border-radius: $bb-spacing-1 !important;
        box-sizing: border-box !important;
      }

      .button-title {
        width: auto !important;
        overflow: visible !important;
        text-overflow: unset !important;
        white-space: nowrap;
      }
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab--selected) {
      background-color: $bb-neutral-0 !important;
      color: $bb-neutral-1 !important;
      border: 1px solid $bb-neutral-4;
    }
  }

  &__add-reference {
    flex-shrink: 0;
    padding: $bb-spacing-2 $bb-spacing-4 $bb-spacing-4;
  }

  &__add-reference-btn {
    width: 100%;

    :deep(button) {
      width: 100%;
      justify-content: center;
    }
  }

  &__add-reference-form {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
  }

  &__add-reference-input {
    width: 100%;

    :deep(.bb-textarea) {
      width: 100% !important;
    }
  }

  &__add-reference-actions {
    display: flex;
    justify-content: flex-end;
    gap: $bb-spacing-2;
  }
}
</style>
